import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  MapPin, Phone, Clock, Car, Train, CheckCircle, Star, ShieldCheck,
  ArrowRight, Smartphone, Laptop, Tablet, AlertCircle,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { findCityBySlug } from "@/data/citiesData";
import {
  TOP_PANNES,
  getPanneBaseSlug,
  buildComboSlug,
} from "@/data/panneVilleData";

const PHONE_LINK = "tel:+33549057490";

export default function CityPage() {
  // Routes are static (generated via CITIES.map in App.js).
  // Extract slug from /reparation-{slug} pathname.
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/reparation-/, "");
  const city = findCityBySlug(slug);

  // 404 sécurité - slug invalide
  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Ville non desservie</h1>
          <p className="text-slate-600 mb-6">Cette ville n&apos;est pas dans notre liste actuelle.</p>
          <Link to="/">
            <Button>Retour à l&apos;accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  // JSON-LD Service spécifique à la ville (impact SEO local)
  const cityServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Réparation iPhone, Mac, ordinateur et téléphone",
    name: `Réparation iPhone, Mac et téléphone à ${city.name}`,
    description: `Mediastore Niort intervient à ${city.name} (${city.postalCode}) pour la réparation iPhone, iPad, MacBook, iMac et toutes marques de smartphones. Service Apple certifié à ${city.distanceKm} km de ${city.name}.`,
    areaServed: {
      "@type": "City",
      name: city.name,
      postalCode: city.postalCode,
      containedInPlace: { "@type": "AdministrativeArea", name: "Deux-Sèvres" },
    },
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.mediastore79.com/#localbusiness",
      name: "Mediastore Niort",
      address: {
        "@type": "PostalAddress",
        streetAddress: "104 Rue de la Gare",
        addressLocality: "Niort",
        postalCode: "79000",
        addressCountry: "FR",
      },
      telephone: "+33549057490",
      url: "https://www.mediastore79.com",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.6",
        reviewCount: "212",
        bestRating: "5",
      },
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "47",
      highPrice: "359",
      offerCount: "30",
    },
  };

  return (
    <div className="min-h-screen" data-testid="city-page">
      <SEO
        title={`Réparation iPhone, Mac & Téléphone ${city.name} - Spécialiste Apple Niort à ${city.distanceKm} km`}
        description={`Réparation iPhone, iPad, MacBook & smartphones depuis ${city.name} (${city.postalCode}). Mediastore Niort, spécialiste Apple certifié à ${city.distanceKm} km (${city.timeMinutes} min par ${city.accessRoad}). 4,6/5 ★ Google (212+ avis). Diagnostic gratuit, garantie 6 mois. ☎ 05 49 05 74 90`}
        keywords={`réparation iPhone ${city.name}, réparation Mac ${city.name}, réparation Apple ${city.name}, réparation téléphone ${city.name}, spécialiste Apple ${city.name}, réparation MacBook ${city.name}, écran iPhone ${city.name}, batterie iPhone ${city.name}, réparation Samsung ${city.name}, réparation Xiaomi ${city.name}, réparation Huawei ${city.name}, dépannage informatique ${city.name}, Mediastore ${city.name}, Apple ${city.name} ${city.postalCode}, ${city.name} Deux-Sèvres`}
        path={`/reparation-${city.slug}`}
        schema={cityServiceSchema}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Villes desservies", path: "/villes-desservies" },
          { name: city.name, path: `/reparation-${city.slug}` },
        ]}
      />

      {/* HERO Section géolocalisée */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-red-500 rounded-full blur-3xl"></div>
        </div>

        <div className="container-custom relative">
          <Badge className="bg-white/10 text-white border border-white/20 px-4 py-2 mb-6 hover:bg-white/10">
            <MapPin className="w-4 h-4 mr-2" />
            {city.name} ({city.postalCode})
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Réparation iPhone, Mac & Téléphone
            <span className="block text-red-400 mt-2">depuis {city.name}</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mb-8 leading-relaxed">
            Mediastore Niort, le <strong className="text-white">spécialiste Apple n°1</strong> en Deux-Sèvres,
            accueille les habitants de <strong className="text-white">{city.name}</strong> à seulement
            <strong className="text-red-400"> {city.distanceKm} km ({city.timeMinutes} minutes)</strong> de
            son atelier au 104 Rue de la Gare à Niort. Techniciens <strong>Apple certifiés</strong>,
            diagnostic gratuit, garantie 6 mois.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href={PHONE_LINK}>
              <Button size="lg" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 h-14 px-8 gap-2 text-lg font-semibold shadow-xl" data-testid="city-call-btn">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/rendez-vous">
              <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 gap-2 border-2 border-white/30 text-white hover:bg-white/10">
                Prendre rendez-vous
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">4.6★</p>
              <p className="text-xs text-slate-300">212+ avis Google</p>
            </div>
            <div className="text-center border-x border-white/20">
              <p className="text-3xl font-bold text-red-400">10+</p>
              <p className="text-xs text-slate-300">ans à Niort</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">6 mois</p>
              <p className="text-xs text-slate-300">garantie incluse</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trajet & Accès - Section UNIQUE par ville (anti-doorway) */}
      <section className="section-padding bg-white" data-testid="city-access-section">
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">
              <Car className="w-4 h-4 mr-1" />
              Comment venir
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {city.name} → Mediastore Niort
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <Card className="border-2 border-red-100">
              <CardContent className="p-6 text-center">
                <Car className="h-10 w-10 text-red-600 mx-auto mb-3" />
                <p className="text-3xl font-bold text-slate-900 mb-1">{city.distanceKm} km</p>
                <p className="text-slate-600">par {city.accessRoad}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-100">
              <CardContent className="p-6 text-center">
                <Clock className="h-10 w-10 text-red-600 mx-auto mb-3" />
                <p className="text-3xl font-bold text-slate-900 mb-1">{city.timeMinutes} min</p>
                <p className="text-slate-600">de trajet en voiture</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-100">
              <CardContent className="p-6 text-center">
                <Train className="h-10 w-10 text-red-600 mx-auto mb-3" />
                <p className="text-lg font-semibold text-slate-900 mb-1">Transports</p>
                <p className="text-sm text-slate-600">{city.publicTransport}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-600" />
                {city.name} en bref
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-slate-700">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Code postal</p>
                  <p className="font-semibold">{city.postalCode}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Population</p>
                  <p className="font-semibold">{city.population}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Intercommunalité</p>
                  <p className="font-semibold">{city.intercommunalite}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Point de repère</p>
                  <p className="font-semibold">{city.landmark}</p>
                </div>
              </div>
              <p className="text-slate-600 mt-6 leading-relaxed">{city.clientStory}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services proposés depuis cette ville */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos services pour les habitants de {city.name}
            </h2>
            <p className="text-lg text-slate-600">
              Diagnostic gratuit, devis transparent, garantie 6 mois
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/reparation-iphone-niort" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                <CardContent className="p-6">
                  <Smartphone className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Réparation iPhone
                  </h3>
                  <p className="text-slate-600 mb-4">Tous modèles iPhone 6 à 15 Pro Max. Écran dès 59 €, batterie dès 49 €.</p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    Voir les tarifs <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/reparation-mac-niort" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                <CardContent className="p-6">
                  <Laptop className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Réparation Mac
                  </h3>
                  <p className="text-slate-600 mb-4">MacBook Pro/Air, iMac, Mac mini. Micro-soudure carte mère. Main d&apos;œuvre 79 €/h.</p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    Voir les tarifs <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/reparation-ipad-niort" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                <CardContent className="p-6">
                  <Tablet className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Réparation iPad
                  </h3>
                  <p className="text-slate-600 mb-4">iPad Pro, Air, mini. Écran dès 155 €, batterie dès 85 €.</p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    Voir les tarifs <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/reparation-multimarque-niort" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                <CardContent className="p-6">
                  <Smartphone className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Samsung, Xiaomi, Huawei...
                  </h3>
                  <p className="text-slate-600 mb-4">Toutes marques Android. Écran AMOLED dès 119 €. OnePlus, Pixel, Honor, Oppo.</p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    Voir les marques <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/reparation-ordinateur" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                <CardContent className="p-6">
                  <Laptop className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Dépannage Informatique
                  </h3>
                  <p className="text-slate-600 mb-4">PC, virus, SSD, récupération données. Diagnostic gratuit, intervention sous 24-48 h.</p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    En savoir plus <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/click-collect" className="block group">
              <Card className="h-full hover:shadow-xl transition-shadow border-2 border-red-300 bg-red-50">
                <CardContent className="p-6">
                  <ShieldCheck className="h-10 w-10 text-red-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    Click & Collect
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Idéal depuis {city.name}. Déposez ou envoyez votre appareil par Colissimo (gratuit &gt; 49 €). Suivi en ligne.
                  </p>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    Réserver <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Pourquoi nous - garanties */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi les habitants de {city.name} nous choisissent
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <Star className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">4,6/5 sur Google</h3>
              <p className="text-sm text-slate-600">212+ avis clients vérifiés</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <CheckCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Diagnostic gratuit</h3>
              <p className="text-sm text-slate-600">Devis transparent sous 24 h</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <ShieldCheck className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Garantie 6 mois</h3>
              <p className="text-sm text-slate-600">Pièces + main d&apos;œuvre</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <Clock className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Réparation Express</h3>
              <p className="text-sm text-slate-600">Dès 30 minutes sur place</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Locale - 2 questions UNIQUES à cette ville */}
      <section className="section-padding bg-slate-50" data-testid="city-faq-section">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes - {city.name}
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-lg text-slate-900 mb-2">{city.localFaqQ}</h3>
              <p className="text-slate-700 leading-relaxed">{city.localFaqA}</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-lg text-slate-900 mb-2">
                Y a-t-il un Apple Store officiel à {city.name} ?
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Il n&apos;y a pas d&apos;Apple Store officiel à {city.name}, ni dans toute la région Deux-Sèvres / Charente-Maritime nord.
                Mediastore Niort est l&apos;alternative la plus proche (à {city.distanceKm} km de {city.name}) avec techniciens
                <strong> Apple certifiés</strong>. Nous proposons les mêmes services qu&apos;un Apple Store : diagnostic gratuit,
                réparation iPhone/iPad/Mac, remplacement batterie, écran, micro-soudure, récupération de données.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-lg text-slate-900 mb-2">
                Faites-vous l&apos;expédition Colissimo pour les clients de {city.name} ?
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Oui. Mediastore Niort propose l&apos;<strong>expédition Colissimo gratuite</strong> au-delà de 49 € de
                réparation pour les clients de {city.name} ({city.distanceKm} km). Vous nous envoyez votre appareil,
                nous établissons un diagnostic gratuit sous 24 h, vous validez le devis en ligne, et nous renvoyons
                l&apos;appareil réparé sous 48-72 h. Suivi temps réel par code MC-XXXX.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-lg text-slate-900 mb-2">
                Quels sont vos horaires d&apos;ouverture ?
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Mediastore Niort est ouvert du <strong>lundi au vendredi de 10h à 18h</strong> et le
                <strong> samedi de 10h à 16h</strong>. Pour les habitants de {city.name}, nous recommandons
                de prendre rendez-vous en ligne pour garantir un créneau et éviter l&apos;attente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pannes courantes traitées depuis cette ville (maillage Phase C) */}
      <section className="section-padding bg-white" data-testid="city-top-pannes">
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-8">
            <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">
              Réparations à {city.name}
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Top 6 des pannes traitées depuis {city.name}
            </h2>
            <p className="text-slate-600 text-sm">
              Pages dédiées avec trajet, route, tarifs et FAQ spécifique aux habitants de {city.name}.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {TOP_PANNES.map((p) => {
              const slug = buildComboSlug(getPanneBaseSlug(p), city.slug);
              const short = p.titleH1.split(" à Niort")[0].split(" - ")[0];
              return (
                <Link
                  key={p.slug}
                  to={`/${slug}`}
                  className="flex items-center justify-between bg-slate-50 hover:bg-red-50 rounded-lg p-4 border border-slate-100 hover:border-red-200 transition-all group"
                  data-testid={`city-top-panne-${p.slug}`}
                >
                  <div>
                    <p className="font-semibold text-slate-900 group-hover:text-red-600 text-sm">{short}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.durationLabel} • dès {p.priceFrom} €</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all" />
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-6">
            <Link to="/reparations-locales" className="text-red-600 text-sm font-medium hover:underline">
              Voir toutes les pannes traitées depuis {city.name} →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à faire réparer votre appareil depuis {city.name} ?
          </h2>
          <p className="text-lg text-red-100 mb-8 max-w-2xl mx-auto">
            Appelez-nous, prenez rendez-vous en ligne ou envoyez-nous votre appareil par Colissimo gratuit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK}>
              <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 h-14 px-8 gap-2 text-lg font-semibold">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="h-14 px-8 gap-2 border-2 border-white text-white hover:bg-white/10">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
